import { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Divider, Grid, MenuItem, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { tokens } from "../../../../../theme";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import type { AxiosResponse } from "axios";
import apiService from "../../../../../service/Api";
import { BookingData } from "../../../types/view-booking";
import { SEOConfigApiItem, SEOConfigsApiResponse } from "../../../../configurations/seo-management/services/seoConfig";
import { SiteListApiItem, SitesApiResponse } from "../../../../configurations/site-listings/services/siteListingConfig";

type Draft = { title: string; description: string; handle: string };
type Defaults = { values: Draft; siteName: string; origin: string; warning: string };

function streamURL(link?: string): URL | null {
  try {
    const url = new URL(link || "");
    return ["https:", "http:"].includes(url.protocol) ? url : null;
  } catch {
    return null;
  }
}

function host(domain: string): string {
  return streamURL(domain.includes("://") ? domain : `https://${domain}`)?.hostname.toLowerCase().replace(/^www\./, "") || "";
}

async function loadSites(): Promise<SiteListApiItem[]> {
  const sites: SiteListApiItem[] = [];
  let next: string | null = "/api/configurations/sites/?page_size=1000";
  while (next) {
    const response: AxiosResponse<SitesApiResponse> = await apiService.get<SitesApiResponse>(next);
    sites.push(...response.data.results);
    // Keep subsequent requests on the configured API origin.
    const nextURL: URL | null = response.data.next ? new URL(response.data.next, "https://api.local") : null;
    next = nextURL ? `${nextURL.pathname}${nextURL.search}` : null;
  }
  return sites;
}

export default function BookingSEO({ bookingData, active }: { bookingData: BookingData; active: boolean }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selected, setSelected] = useState(0);
  const [defaults, setDefaults] = useState<Defaults[]>([]);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const streams = bookingData.streams;

  useEffect(() => {
    if (!active || loaded || !streams.length) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const sites = await loadSites();
        const configs = new Map<number, SEOConfigApiItem | undefined>();
        const values = await Promise.all(streams.map(async (stream): Promise<Defaults> => {
          const url = streamURL(stream.website_stream_link);
          const streamHost = url ? host(url.origin) : "";
          const site = sites.find((item) => host(item.domain) === streamHost);
          let config: SEOConfigApiItem | undefined;
          if (site) {
            if (!configs.has(site.id)) {
              const response = await apiService.get<SEOConfigsApiResponse>(
                "/api/configurations/seo-configs/",
                { site: site.id, page_name: "stream_page", is_active: true, page_size: 1000 },
              );
              configs.set(site.id, response.data.results[0]);
            }
            config = configs.get(site.id);
          }
          const date = stream.date ? new Date(stream.date.slice(0, 10) + "T12:00:00") : null;
          const context: Record<string, string> = {
            deceased_name: bookingData.beloved?.name || stream.title,
            stream_date: date && !Number.isNaN(date.getTime())
              ? date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : stream.date || "",
            deceased_image: bookingData.beloved?.avatar || "",
          };
          const resolve = (template: string) => template.replace(/\{\{(\w+)\}\}/g, (token, key) => context[key] ?? token);
          return {
            values: {
              title: config ? resolve(config.meta_title_template) : stream.title,
              description: config ? resolve(config.meta_description_template) : "",
              handle: url?.pathname.replace(/^\//, "") || "",
            },
            origin: url?.origin || "",
            siteName: site?.name || url?.hostname || "Stream page",
            warning: !url ? "This stream has no website URL yet. A URL preview will be available once it is configured."
              : !site ? "No matching website configuration was found for this stream."
              : !config ? "No active stream-page SEO defaults were found for this website." : "",
          };
        }));
        if (!cancelled) {
          setDefaults(values);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setError("Could not load the website SEO defaults. Retry to load the configured values.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [active, loaded, streams, bookingData.beloved, attempt]);

  if (!streams.length) return <Alert severity="info">SEO settings will be available once a stream is added to this booking.</Alert>;
  if (loading) return <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 5 }}><CircularProgress size={24} /><Typography>Loading stream SEO defaults…</Typography></Stack>;
  if (error) return <Alert severity="warning" action={<Button color="inherit" onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}>{error}</Alert>;
  const current = defaults[selected];
  if (!current) return null;
  const draft = drafts[selected] || current.values;
  const changed = Object.keys(current.values).some((key) => draft[key as keyof Draft] !== current.values[key as keyof Draft]);
  const update = (key: keyof Draft, value: string) => setDrafts((previous) => ({ ...previous, [selected]: { ...draft, [key]: value } }));
  const handle = draft.handle.replace(/^\/+/, "");
  const fullURL = current.origin ? `${current.origin}/${handle}` : "";

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 9 }} sx={{ minWidth: 0 }}>
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ borderRadius: "12px", p: { xs: 2, sm: 3 } }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ display: "flex", p: 1.25, borderRadius: "10px", bgcolor: colors.primary[900], color: colors.primary[400] }}>
                    <TuneOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Stream SEO</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Manage how this stream appears in search results.</Typography>
                  </Box>
                </Stack>
                {streams.length > 1 && (
                  <TextField select label="Stream" size="small" value={selected} onChange={(event) => setSelected(Number(event.target.value))} sx={{ minWidth: 200, maxWidth: { sm: 300 } }}>
                    {streams.map((stream, index) => <MenuItem key={stream.id ?? index} value={index}>{stream.title || `Stream ${index + 1}`}</MenuItem>)}
                  </TextField>
                )}
              </Stack>
            </Paper>
            {current.warning && <Alert severity="warning" sx={{ borderRadius: "12px" }}>{current.warning}</Alert>}
            <Paper elevation={0} sx={{ borderRadius: "12px", overflow: "hidden" }}>
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Search engine details</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>Start with your website defaults and tailor the details for this stream.</Typography>
              </Box>
              <Divider />
              <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}>
                <TextField fullWidth label="Page title" value={draft.title} onChange={(event) => update("title", event.target.value)} error={draft.title.length > 70} helperText={`${draft.title.length} of 70 recommended characters used`} />
                <TextField fullWidth multiline minRows={5} label="Meta description" value={draft.description} onChange={(event) => update("description", event.target.value)} error={draft.description.length > 160} helperText={`${draft.description.length} of 160 recommended characters used`} />
                <TextField fullWidth label="URL handle" placeholder="memorial-page/stream-name" value={draft.handle} onChange={(event) => update("handle", event.target.value)} helperText={fullURL || "No website URL configured for this stream"} slotProps={{ formHelperText: { sx: { overflowWrap: "anywhere" } } }} />
              </Stack>
              <Divider />
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: changed ? "warning.main" : colors.primary[400] }} />
                  <Typography variant="body2" color="text.secondary">{changed ? "Unsaved preview changes" : "Using stream defaults"}</Typography>
                </Stack>
                <Button variant="outlined" startIcon={<RestartAltIcon />} disabled={!changed} onClick={() => setDrafts((previous) => { const next = { ...previous }; delete next[selected]; return next; })} sx={{ textTransform: "none", borderRadius: 2, color: colors.primary[400], borderColor: colors.primary[400], fontWeight: 600 }}>Reset to defaults</Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} component="aside" aria-label="SEO preview" sx={{ minWidth: 0, position: { md: "sticky" }, top: { md: 24 } }}>
          <Stack spacing={2}>
            <Paper elevation={0} sx={{ borderRadius: "12px", overflow: "hidden" }}>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <SearchOutlinedIcon sx={{ fontSize: 20, color: colors.primary[400] }} />
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Search engine listing</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">A preview of your stream in search results.</Typography>
              </Box>
              <Divider />
              <Box aria-live="polite" sx={{ p: 2, overflowWrap: "anywhere" }}>
                <Typography fontSize={13} fontWeight={600}>{current.siteName}</Typography>
                <Typography color="text.secondary" fontSize={11} sx={{ mt: 0.5, lineHeight: 1.6 }}>{fullURL ? `${current.origin} › ${handle.split("/").filter(Boolean).join(" › ")}` : "Stream URL not configured"}</Typography>
                <Typography sx={{ color: theme.palette.mode === "dark" ? "#8ab4f8" : "#1a0dab", fontSize: 18, lineHeight: 1.4, mt: 1.5, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2, overflow: "hidden" }}>{draft.title || "Page title"}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: 13, mt: 1, lineHeight: 1.7, whiteSpace: "pre-wrap", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4, overflow: "hidden" }}>{draft.description || "Add a description to preview your search listing."}</Typography>
              </Box>
              <Box sx={{ px: 2, py: 1.5, bgcolor: colors.primary[900] }}>
                <Chip size="small" label="Live preview" sx={{ bgcolor: "background.paper", color: colors.primary[400], fontSize: 11, fontWeight: 600 }} />
                <Typography sx={{ mt: 1, fontSize: 11, lineHeight: 1.6, color: "text.secondary" }}>Search engines may display a different title or description.</Typography>
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ borderRadius: "12px", p: 2 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Preview only</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>Changes are kept while this booking is open and are not saved or published.</Typography>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
